import React, { FunctionComponent, useEffect, useState } from 'react';
import WhiteCard from 'global/atoms/WhiteCard';
import { IHeader, IPagination, ISelectViewResponse, ISort } from 'utils/models';
import { defaultFilterOrderState, defaultFilterPaginateState, selectViewResponseState } from 'utils/states';
import { handleToast } from 'utils/helpers';
import { useHistory, useParams } from 'react-router-dom';
import { Cell, Column, HeaderCell } from 'rsuite-table';
import List from 'global/table/List';
import BadgeStatus from 'global/atoms/badge/BadgeStatus';
import IconButton from 'global/atoms/IconButton';
import Pagination from 'global/pagination/Pagination';
import Filters from 'global/filters/Filters';
import Input from 'global/atoms/Input';
import DayPickerInputCustom from 'global/atoms/DayPickerInputCustom';
import Select from 'global/atoms/Select';
import OrderConnection from 'utils/connections/orders';
import { handleClearFilters, handleSubmitAndSaveFilters, handleToggleFilters, loadFilters } from 'utils/filterHelper';
import ButtonFilters from '../../../../global/atoms/ButtonFilters';
import HeaderButtons from '../../../../global/atoms/headerButtons/HeaderButtons';
import Spinner from 'global/atoms/Spinner/Spinner';
import Button from 'global/atoms/Button';
import {
  DeleteSelectedButton,
  DeleteSingleButton,
  ToggleAllCheckbox,
  ToggleSingleCheckbox,
} from '../../../../utils/toggleToDeleteHelper';
import { confirmModal } from '../../../../redux/store';
import ActionsContainer from '../../../../global/atoms/ActionsContainer';
import ButtonListRedirect from '../../../../global/atoms/ButtonListRedirect';

export interface IOrderFilters {
  customerId: string;
  locationId: string;
  userId: string;
  statusId: string;
  documentNumber: string;
  dateFrom: string;
  dateTo: string;
  requestPaginate: IPagination;
  requestOrder: ISort;
}

interface IOrderData {
  customer: any;
  deliveryDate: string;
  documentDate: string;
  orderId: string;
  documentNumber: string;
  status: any;
  paymentDate: string;
  positions: any;
  user: any;
}

interface IOrdersData {
  columns: any;
  header: IHeader;
  data: Array<IOrderData>;
  count: number;
}

const OrdersList: FunctionComponent = () => {
  const [data, setData] = useState<IOrdersData | null>(null);
  const defaultFilters: IOrderFilters = {
    customerId: '',
    locationId: '',
    userId: '',
    statusId: '',
    documentNumber: '',
    dateFrom: '',
    dateTo: '',
    ...defaultFilterOrderState,
    ...defaultFilterPaginateState,
  };
  const [filters, setFilters] = useState<IOrderFilters>(loadFilters(defaultFilters));
  const [reloadData, setReloadData] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [toggleFilters, setToggleFilters] = useState<boolean>(false);
  const [chosenVisits, setChosenVisits] = useState<Array<string>>([]);
  const [form, setForm] = useState<{
    customers: ISelectViewResponse;
    locations: ISelectViewResponse;
    orderStatuses: ISelectViewResponse;
    users: ISelectViewResponse;
  }>({
    customers: selectViewResponseState,
    locations: selectViewResponseState,
    orderStatuses: selectViewResponseState,
    users: selectViewResponseState,
  });

  useEffect(() => {
    OrderConnection.filterForm(id).then((data) => {
      setForm(data.data);
    });
  }, []);

  const loadData = () => {
    const filtersClone = { ...filters };
    filtersClone.dateFrom = `${filtersClone.dateFrom}`;
    filtersClone.dateTo = `${filtersClone.dateTo}`;

    OrderConnection.list(id, filtersClone).then((data) => {
      setData(data.data);
    });
  };

  useEffect(() => loadData(), [filters.requestPaginate]);

  useEffect(() => {
    if (reloadData) {
      loadData();
      setReloadData(false);
    }
  }, [reloadData]);

  const handleOpenOrder = (orderId: string, event: any) => {
    event.stopPropagation();
    history.push(`/projects/${id}/orders/${orderId}/details`);
  };

  const handleSend = () => {
    if (!chosenVisits.length) {
      return;
    }
    OrderConnection.sendOrders({ orders: chosenVisits.map((item) => ({ id: item })) }, id).then(() => {
      loadData();
      setChosenVisits([]);
    });
  };

  if (data === null) return <Spinner />;

  const handleDeleteSelected = (deleteId: string) => {
    let toDelete = chosenVisits.map((id) => ({ id: id }));
    if (deleteId !== undefined) {
      toDelete = [{ id: deleteId }];
    }
    handleToast(OrderConnection.removeOrders({ orders: toDelete }, id)).then(() => {
      setChosenVisits([]);
      loadData();
    });
  };

  return (
    <>
      <HeaderButtons>
        <ButtonFilters onClick={handleToggleFilters.bind(null, setToggleFilters)} />
        <Button onClick={confirmModal('Czy wysłać zaznaczone zamówienia?', handleSend)} disabled={!chosenVisits.length}>
          Wyślij zanzaczone
        </Button>
        <DeleteSelectedButton chosenToDelete={chosenVisits} callback={handleDeleteSelected} form={form} />
      </HeaderButtons>

      <WhiteCard mobileTransparent={true} padding={false}>
        <List data={data.data} count={data.data.length}>
          <Column width={40} align="left">
            <HeaderCell depth={0} width={0}>
              <ToggleAllCheckbox
                data={data.data}
                state={chosenVisits}
                setState={setChosenVisits}
                keyIdName={'orderId'}
              />
            </HeaderCell>
            <Cell width={100} depth={0}>
              {(rowData) => (
                <ToggleSingleCheckbox state={chosenVisits} setState={setChosenVisits} selectedId={rowData.orderId} />
              )}
            </Cell>
          </Column>
          <Column flexGrow={2} width={130} align="left">
            <HeaderCell depth={0} width={0}>
              Numer
            </HeaderCell>
            <Cell width={100} depth={0}>
              {(rowData) => (
                <ButtonListRedirect redirectUrl={`/projects/${id}/orders/${rowData.orderId}/details`}>
                  {rowData.documentNumber}
                </ButtonListRedirect>
              )}
            </Cell>
          </Column>
          <Column flexGrow={2} width={130} align="left">
            <HeaderCell depth={0} width={0}>
              Dystrybutor
            </HeaderCell>
            <Cell width={100} depth={0}>
              {(rowData) => {
                return <span>{rowData.customer.name}</span>;
              }}
            </Cell>
          </Column>
          <Column flexGrow={2} minWidth={130} align="left">
            <HeaderCell depth={0} width={0}>
              Odbiorca
            </HeaderCell>
            <Cell width={100} depth={0}>
              {(rowData) => {
                return <span>{rowData.location.name}</span>;
              }}
            </Cell>
          </Column>
          <Column width={70} align="left">
            <HeaderCell depth={0} width={0}>
              Netto
            </HeaderCell>
            <Cell width={70} depth={0}>
              {(rowData) => {
                return <span>{Number(rowData.price.amount).toFixed(2)}</span>;
              }}
            </Cell>
          </Column>
          <Column width={80} align="left">
            <HeaderCell depth={0} width={0}>
              Vat
            </HeaderCell>
            <Cell width={80} depth={0}>
              {(rowData) => {
                return <span>{Number(rowData.price.tax).toFixed(2)}</span>;
              }}
            </Cell>
          </Column>
          <Column width={80} align="left">
            <HeaderCell depth={0} width={0}>
              Brutto
            </HeaderCell>
            <Cell width={80} depth={0}>
              {(rowData) => {
                return <span>{Number(rowData.price.amountGross).toFixed(2)}</span>;
              }}
            </Cell>
          </Column>
          <Column minWidth={150} align="left">
            <HeaderCell depth={0} width={0}>
              Wystawił
            </HeaderCell>
            <Cell width={150} depth={0}>
              {(rowData) => {
                return <span>{rowData.user.fullName}</span>;
              }}
            </Cell>
          </Column>
          <Column width={120} align="left">
            <HeaderCell depth={0} width={0}>
              Status
            </HeaderCell>
            <Cell width={120} depth={0}>
              {(rowData) => <BadgeStatus color={rowData.status.color}>{rowData.status.name}</BadgeStatus>}
            </Cell>
          </Column>
          <Column width={120} align="left">
            <HeaderCell depth={0} width={0}>
              Data
            </HeaderCell>
            <Cell width={120} depth={0} dataKey="documentDate" />
          </Column>
          <Column fixed="right" width={70} align="left">
            <HeaderCell depth={0} width={0}>
              Akcje
            </HeaderCell>
            <Cell width={100} depth={0}>
              {(rowData) => (
                <ActionsContainer>
                  <IconButton icon={'arrowRightBig'} onClick={handleOpenOrder.bind(null, rowData.orderId)} />
                  <DeleteSingleButton callback={handleDeleteSelected.bind(null, rowData.orderId)} form={form} />
                </ActionsContainer>
              )}
            </Cell>
          </Column>
        </List>
      </WhiteCard>
      <Pagination
        count={data.count}
        limit={filters.requestPaginate.limit.toString()}
        page={filters.requestPaginate.page.toString()}
        setState={setFilters}
      />
      <Filters
        toggleFilters={toggleFilters}
        handleToggleFilters={handleToggleFilters.bind(null, setToggleFilters)}
        handleSubmit={handleSubmitAndSaveFilters.bind(null, setReloadData, filters)}
        handleClear={handleClearFilters.bind(null, setFilters, defaultFilters, setReloadData)}
      >
        <Input
          value={filters.documentNumber}
          placeholder={'Numer'}
          name={'documentNumber'}
          type={'text'}
          setState={setFilters}
          state={filters}
        />
        <DayPickerInputCustom
          value={filters.dateFrom}
          placeholder={'Data od'}
          name={'dateFrom'}
          setState={setFilters}
          state={filters}
        />
        <DayPickerInputCustom
          value={filters.dateTo}
          placeholder={'Data do'}
          name={'dateTo'}
          setState={setFilters}
          state={filters}
        />
        <Select
          placeholder={'Status'}
          name={'statusId'}
          value={filters.statusId}
          options={form.orderStatuses.options}
          state={filters}
          setState={setFilters}
        />
        <Select
          placeholder={'Dystrybutor'}
          name={'customerId'}
          value={filters.customerId}
          options={form.customers.options}
          state={filters}
          setState={setFilters}
        />
        <Select
          placeholder={'Odbiorca'}
          name={'locationId'}
          value={filters.locationId}
          options={form.locations.options}
          state={filters}
          setState={setFilters}
        />
        <Select
          placeholder={'Wystawił'}
          name={'userId'}
          value={filters.userId}
          options={form.users.options}
          state={filters}
          setState={setFilters}
        />
      </Filters>
    </>
  );
};

export default OrdersList;
